import { useMemo } from 'react';
import { Alert } from '@trussworks/react-uswds';

import ReadOnlyMap from '@components/map/readOnlyMap';
import ExternalLink from '@components/external-link/ExternalLink';

import { formatUrl, getStateNameFromCode, calculateGeometryExtent } from '@src/utils/helpers';
import PublicNoticeLink from './public-notice-link/PublicNoticeLink';
import LabelValue from '@components/label-value/LabelValue';

const PublicNoticeInfo = ({ data, districtData }) => {
  const statesLabel = data?.state?.length > 1 ? 'States' : 'State';
  const statesValue = data?.state?.map((code) => getStateNameFromCode(code)).join(', ');
  const geometry = useMemo(
    () => ({
      type: 'Point',
      coordinates: [data?.longitude, data?.latitude],
    }),
    [data?.latitude, data?.longitude]
  );

  return (
    <div className='info-container'>
      <div>
        <section className='info-section flex-item'>
          <h3>Details</h3>
          <div className='info-flex-container'>
            <div className='info-flex-item'>
              <LabelValue label='DA Number' value={data.daNumber} />
              <LabelValue label='Project Name' value={data.projectName} />
              <LabelValue label='Action Type' value={data.actionType} />
            </div>
            <div className='info-flex-item'>
              <LabelValue label='Date of Public Notice' value={data.startDate} isDate />
              <LabelValue label='Comment Period Ends' value={data.commentsEndDate} isDate />
              <LabelValue label='Keyword(s)' value={data.keywords} />
            </div>
          </div>
          <LabelValue label='Description' value={data?.description ?? 'No Description Available'} />
        </section>
        <section className='info-section flex-item'>
          <h3>Applicant</h3>
          <div className='info-flex-container'>
            <div className='info-flex-item'>
              <LabelValue label='Applicant Name' value={data.applicantName} />
            </div>
            <div className='info-flex-item'>
              <LabelValue label='Applicant Company' value={data.applicantCompany} />
            </div>
          </div>
        </section>
      </div>
      <section className='info-section'>
        <h3>Public Notice Links</h3>
        {districtData && (
          <Alert noIcon slim className='callout margin-bottom-1 text-italic'>
            Contact the{' '}
            <ExternalLink title={districtData.orgName} content={districtData.orgName} href={districtData.orgUrl} /> at{' '}
            <a href='tel:{districtData.orgPhone}'>{districtData.orgPhone}</a> to report broken links or obtain
            additional help.
          </Alert>
        )}
        <div className='public-notice-link-container'>
          {data?.urls?.map((url) => (
            <div key={url}>
              <PublicNoticeLink content={url} href={formatUrl(url)} />
            </div>
          ))}
        </div>
      </section>
      <section className='info-section'>
        <h3>Location</h3>
        <div className='info-flex-container'>
          <div className='info-flex-item'>
            <ReadOnlyMap geometry={geometry} extent={calculateGeometryExtent(geometry)} height='240px' />
          </div>
          <div className='info-flex-item'>
            <div className='info-flex-container'>
              <div className='info-flex-item'>
                <LabelValue label='District' value={data.district} />
                <LabelValue label={statesLabel} value={statesValue} />
                <LabelValue label='County' value={data.county} />
                <LabelValue label='Zip Code' value={data.zipCode} />
              </div>
              <div className='info-flex-item'>
                <LabelValue label='Hydrologic Unit Code' value={data.hydrologicUnitCode} />
                <LabelValue label='Latitude' value={data.latitude} />
                <LabelValue label='Longitude' value={data.longitude} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicNoticeInfo;
